/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { Component } from 'react';
import {
    string, func, object, number, shape, arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import Grid from '@material-ui/core/Grid';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import { getAccessToken } from '../../../../state/ducks/App/ducks/Common/Common-Selectors';
import { applePayDateConverter } from '../../../helpers/applePayShippingOptionsConverter';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import {
    getCartData,
    getApplePayStatus,
    getNoOfOrderItems,
    getOrderTotalSummary,
    getOrderId,
    getSelectedMovie,
    getGiftMessageInfo,
    getAppliedPromotions,
} from '../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { checkApplePayEnabled } from '../../../../state/ducks/Checkout/ducks/Checkout/Checkout-Actions';
import { deleteItem } from '../../../../state/ducks/Checkout/ducks/Cart/ducks/DeleteItem/DeleteItem-Actions';
import { loadCart, getCart } from '../../../../state/ducks/Checkout/ducks/Cart/Cart-Actions';
import { loadOrderId } from '../../../../state/ducks/Checkout/ducks/Order/Order-Actions';
import { processNBCUMovie, applePayOrderSuccess, applePayOrderValidateMerchantSuccess } from '../../../../state/ducks/Checkout/ducks/Payment/Payment-Actions';
import orderClient from '../../../../apis/checkout-apis/orderClient';

class GraphqlApplePay extends Component {
    constructor(props) {
        super(props);
        this.state = {
            cartEmpty: false,
        };
        // The following variables are not state variables because assignment to them has to be syncronous
        // and their modification should not trigger a rerender of the component.
        this.price = '';
        this.shipping = '';
        this.tax = '';
        this.total = 0;
        this.orderItemId = '';
        this.orderId = '';
        this.cartData = {};
        this.applePayResponse = {};
    }

    componentDidMount() {
        const { numberOfOrderItems, checkApplePay, location } = this.props;
        if ((location.pathname.includes('checkout') || numberOfOrderItems === 0) && (typeof window !== 'undefined' && !window.location.host.includes('localhost'))) {
            this.setState({
                cartEmpty: true,
            }, () => { checkApplePay(); });
        }
    }

    getLineItem = (name, value) => {
        const lineItem = {};
        lineItem.label = name;
        lineItem.amount = value;
        return lineItem;
    };

    getLineItems = () => {
        const lineItems = [
            this.getLineItem('Item Price', typeof this.price === 'number' ? this.price.toFixed(2) : this.price),
            this.getLineItem('Shipping', typeof this.shipping === 'number' ? this.shipping.toFixed(2) : this.shipping),
            this.getLineItem('Tax', typeof this.tax === 'number' ? this.tax.toFixed(2) : this.tax),
        ];
        this.total = Number(this.price) + Number(this.shipping) + Number(this.tax);

        return lineItems;
    };

    getLineItemsCheckout = () => {
        const {
            orderSummary: {
                totalProduct, totalShipping, totalService, totalTax,
            },
            appliedPromos,
        } = this.props;
        const lineItems = [];

        lineItems.push(this.getLineItem('Item Price', typeof totalProduct === 'number' ? totalProduct.toFixed(2) : totalProduct));

        if (parseFloat(totalShipping) > 0) {
            lineItems.push(this.getLineItem('Shipping', parseFloat(totalShipping).toFixed(2)));
        }

        if (parseFloat(totalService) > 0) {
            lineItems.push(this.getLineItem('Service', parseFloat(totalService).toFixed(2)));
        }

        if (appliedPromos.appliedPromotions.length > 0) {
            appliedPromos.appliedPromotions.forEach(
                (promotion) => lineItems.push(this.getLineItem('Promotion', parseFloat(promotion.appliedAmount).toFixed(2))),
            );
        }

        lineItems.push(this.getLineItem('Tax', typeof totalTax === 'number' ? totalTax.toFixed(2) : totalTax));

        return lineItems;
    };

    addItemToCart = async () => {
        const {
            jwt, selectedSku, saveCartData, setOrderId, brand, giftMessage,
        } = this.props;

        const addToCartResp = await orderClient.addToCart(
            {},
            jwt,
            null, // recipients field
            {
                brandCode: brand?.id,
                productCode: selectedSku?.id,
                quantity: 1,
            },
            '', // giftHistoryOrderItemId
        );

        mbpLogger.logDebug({
            function: 'addItemToCart',
            module: 'mbp-pwa-ui',
            props: this.props,
            message: 'Graphql Apple Pay - added item to cart!',
            addToCartResp,
        });

        mbpLogger.logDebug({
            function: 'addItemToCart',
            module: 'mbp-pwa-ui',
            props: this.props,
            message: 'Graphql Apple Pay - gift message!',
            giftMessage,
            orderId: addToCartResp.data.orderId,
            orderItemId: addToCartResp.data.orderItemId,
        });

        // gift message
        if (
            giftMessage && giftMessage.message && giftMessage.message !== ''
            && addToCartResp?.data?.orderId?.[0]
            && addToCartResp?.data?.orderItemId?.[0]
        ) {
            const addedGiftMessage = await orderClient.updateCart(
                {},
                jwt,
                addToCartResp?.data?.orderItemId?.[0],
                null,
                null,
                {
                    message: giftMessage.message,
                    occasionCode: giftMessage?.occasionCode,
                },
                addToCartResp.data.orderId[0],
            );
            mbpLogger.logDebug({
                function: 'addItemToCart',
                module: 'mbp-pwa-ui',
                props: this.props,
                message: 'Graphql Apple Pay gift message!',
                addedGiftMessage,
                msg: giftMessage.message,
                occasionCode: giftMessage?.occasionCode,
            });
        }

        setOrderId(addToCartResp.data.orderId);
        const cartResponse = await orderClient.getOrderDetails(
            {},
            jwt,
            addToCartResp.data.orderId,
        );
        const { data } = cartResponse;
        saveCartData(data);
        return data;
    }

    createInitialPaymentRequest = (locationInApp = 'pdp') => {
        const {
            brand,
            selectedSku: { prices },
            orderSummary: { orderTotalBalance },
        } = this.props;
        let paymentRequest;
        if (locationInApp === 'pdp') {
            const price = prices.find((sku) => sku.type === 'sale').value;
            paymentRequest = {
                currencyCode: 'USD',
                countryCode: 'US',
                requiredShippingContactFields: ['postalAddress', 'email', 'phone', 'name'],
                requiredBillingContactFields: ['postalAddress', 'phone'],
                total: { label: `${brand['display-name']}`, amount: price },
                supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
                merchantCapabilities: ['supports3DS'],
            };
        } else {
            paymentRequest = {
                currencyCode: 'USD',
                countryCode: 'US',
                requiredShippingContactFields: ['email', 'phone'],
                requiredBillingContactFields: ['postalAddress', 'phone'],
                total: { label: `${brand['display-name']}`, amount: typeof orderTotalBalance === 'string' ? Number(orderTotalBalance) : orderTotalBalance },
                supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
                merchantCapabilities: ['supports3DS'],
            };
        }
        return paymentRequest;
    };

    getFirstOfTheMonth = () => {
        const today = new Date();
        // Need to add 1 to the month because javascript months are 0 indexed (jan is 0)
        // But backend is not 0 indexed (jan is 1)
        let month = today.getMonth() + 1;
        if (month < 10) {
            month = `0${month}`;
        }
        return `${today.getFullYear()}-${month}-01`;
    }

    generateNewShippingMethods = async (calendarDates) => {
        const { jwt } = this.props;
        let shipDateWithoutSurcharge;
        for (let i = 0; i < calendarDates.length; i += 1) {
            if (Number(calendarDates[i].surcharge) === 0 || Number(calendarDates[i].totSurcharge) === 0) {
                shipDateWithoutSurcharge = applePayDateConverter([calendarDates[i]])[0];
                break;
            }
        }
        this.applePayResponse.shippingMethod = shipDateWithoutSurcharge;
        const baseShippingCall = await orderClient.saveShipMethodApplePay(
            {},
            jwt,
            {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                response: JSON.stringify(this.applePayResponse),
            },
        );
        const baseShipping = baseShippingCall.data.shipping;
        const newShippingMethods = applePayDateConverter(
            calendarDates,
            baseShipping,
        );
        return newShippingMethods;
    }

    setDefaultShipDate = async () => {
        const { jwt } = this.props;
        const defaultShipDate = await orderClient.saveShipMethodApplePay(
            {},
            jwt,
            {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                response: JSON.stringify(this.applePayResponse),
            },
        );
        this.shipping = defaultShipDate.data.shipping;
        this.tax = defaultShipDate.data.tax;
        this.total = Number(this.tax) + Number(this.shipping) + Number(this.price);
    }

    handleCheckoutButtonClick = () => {
        const {
            jwt, history, brand, orderId, orderSummary: { orderTotalBalance }, movieItemInCart,
            actionProcessNBCUMovie, finishApplePay, finishApplePayValidateMerchant,
        } = this.props;

        mbpLogger.logDebug({
            function: 'handlePDPButtonClick',
            module: 'mbp-pwa-ui',
            props: this.props,
            message: 'Graphql Apple Pay Init!',
        });

        const initialPaymentRequest = this.createInitialPaymentRequest('checkout');
        const session = new window.ApplePaySession(1, initialPaymentRequest);
        session.onvalidatemerchant = async (event) => {
            const merchantValData = {
                displayName: `${brand['display-name']}`,
                domainName: `${window.location.host}`,
                merchantIdentifier: `${mbpUtil.getEnv('APP_APPLE_PAY_MERCHANT_ID')}`,
                validationURL: event.validationURL,
            };
            orderClient.validateMerchantApplePay({}, jwt, merchantValData)
                .then((merchantSession) => {
                    session.completeMerchantValidation(merchantSession.data);
                    finishApplePayValidateMerchant();
                })
                .catch((er) => {
                    // What do we want to do here if this fails.
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay onvalidatemerchant',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on merchant validate API call failed',
                    });
                });
        };
        session.onpaymentmethodselected = (event) => {
            this.applePayResponse.paymentMethod = event.paymentMethod;

            orderClient.savePayMethodApplePay({}, jwt, {
                orderId,
                orderTotal: typeof orderTotalBalance === 'number' ? this.orderTotalBalance.toFixed(2) : orderTotalBalance,
                response: JSON.stringify(this.applePayResponse),
                reviewPage: true,
            })
                .then(() => {
                    const lineItems = this.getLineItemsCheckout();
                    session.completePaymentMethodSelection(
                        {
                            label: `${brand['display-name']}`,
                            amount: typeof orderTotalBalance === 'number' ? this.orderTotalBalance.toFixed(2) : orderTotalBalance,
                            type: 'final',
                        },
                        lineItems,
                    );
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay onpaymentmethodselected',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on payment selected API call failed',
                    });
                });
        };

        session.onpaymentauthorized = async (event) => {
            this.applePayResponse.payment = event.payment;
            orderClient.authorizeApplePay({}, jwt, {
                orderId,
                orderTotal: typeof orderTotalBalance === 'number' ? this.orderTotalBalance.toFixed(2) : orderTotalBalance,
                response: JSON.stringify(this.applePayResponse),
                reviewPage: true,
            })
                .then(() => {
                    orderClient.placeOrderApplePay({}, jwt, {
                        orderId,
                    })
                        .then(() => {
                            const status = window.ApplePaySession.STATUS_SUCCESS;
                            session.completePayment(status);

                            mbpLogger.logDebug({
                                function: 'onpaymentauthorized',
                                module: 'mbp-pwa-ui',
                                message: 'Graphql Apple Pay - before redirect',
                                statusAppleSession: window.ApplePaySession,
                            });
                            if (movieItemInCart) {
                                actionProcessNBCUMovie(true, history);
                            } else {
                                finishApplePay(history);
                            }
                        })
                        .catch((er) => {
                            mbpLogger.logError({
                                function: 'Graphql Apple Pay placeOrderApplePay',
                                module: 'mbp-pwa-ui',
                                jsError: er,
                                orderId: this.orderId,
                                message: 'on payment authorized API call failed',
                            });
                        });
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay authorizeApplePay',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        event,
                        message: 'on payment authorized API call failed',
                    });
                });
        };
        session.begin();
    }

    handlePDPButtonClick = () => {
        const {
            jwt, clearCart, brand, history, selectedSku: { prices }, actionGetCart,
            finishApplePay, finishApplePayValidateMerchant,
        } = this.props;

        mbpLogger.logDebug({
            function: 'handlePDPButtonClick',
            module: 'mbp-pwa-ui',
            props: this.props,
            message: 'Graphql Apple Pay Init!',
        });

        const initialPaymentRequest = this.createInitialPaymentRequest();
        this.price = prices.find((sku) => sku.type === 'sale').value;
        const session = new window.ApplePaySession(1, initialPaymentRequest);
        session.onvalidatemerchant = async (event) => {
            const merchantValData = {
                displayName: `${brand['display-name']}`,
                domainName: `${window.location.host}`,
                merchantIdentifier: `${mbpUtil.getEnv('APP_APPLE_PAY_MERCHANT_ID')}`,
                validationURL: event.validationURL,
            };
            this.cartData = await this.addItemToCart();
            this.orderItemId = this.cartData.orderDetails.fdOrderVO.recipients[0].orderItems[0].orderItemsId;
            this.orderId = this.cartData.orderDetails.fdOrderVO.orderId;
            orderClient.validateMerchantApplePay({}, jwt, merchantValData)
                .then((merchantSession) => {
                    session.completeMerchantValidation(merchantSession.data);
                    finishApplePayValidateMerchant();
                })
                .catch((er) => {
                    clearCart(this.orderItemId);
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay onvalidatemerchant',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on merchant validate API call failed',
                    });
                });
        };
        session.onshippingcontactselected = (event) => {
            this.applePayResponse.shippingContact = event.shippingContact;
            orderClient.saveRecipientAddressApplePay({}, jwt, {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                response: JSON.stringify(this.applePayResponse),
            })
                .then(() => {
                    const startDate = this.getFirstOfTheMonth();
                    orderClient.getDeliveryCalendar({}, jwt, this.orderItemId, startDate, this.orderId)
                        .then(async ({ data }) => {
                            const { calendarDates } = data.response;
                            const newShippingMethods = await this.generateNewShippingMethods(calendarDates);
                            this.applePayResponse.shippingMethod = newShippingMethods[0];
                            await this.setDefaultShipDate();
                            const lineItems = this.getLineItems();
                            const status = window.ApplePaySession.STATUS_SUCCESS;
                            session.completeShippingContactSelection(
                                status,
                                newShippingMethods,
                                {
                                    label: `${brand['display-name']}`,
                                    amount: this.total.toFixed(2),
                                    type: 'final',
                                },
                                lineItems,
                            );
                        })
                        .catch((er) => {
                            mbpLogger.logError({
                                function: 'Graphql Apple Pay onshippingcontactselected',
                                module: 'mbp-pwa-ui',
                                jsError: er,
                                message:
                            'on shipping contact or delivery calendar API call failed',
                            });
                        });
                });
        };
        session.onshippingmethodselected = (event) => {
            this.applePayResponse.shippingMethod = event.shippingMethod;
            if (GRAPHQL_ENV === 'staging') {
                console.log('in onshippingmethod');
            }
            orderClient.saveShipMethodApplePay({}, jwt, {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                response: JSON.stringify(this.applePayResponse),
            })
                .then(({ data }) => {
                    if (GRAPHQL_ENV === 'staging') {
                        console.log('shipping method data returns');
                    }
                    this.shipping = data.shipping;
                    this.tax = data.tax;
                    const lineItems = this.getLineItems();
                    const status = window.ApplePaySession.STATUS_SUCCESS;
                    session.completeShippingMethodSelection(
                        status,
                        {
                            label: `${brand['display-name']}`,
                            amount: this.total.toFixed(2),
                            type: 'final',
                        },
                        lineItems,
                    );
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay onshippingmethodselected',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on shipping method selected API call failed',
                    });
                });
        };
        session.onpaymentmethodselected = (event) => {
            this.applePayResponse.paymentMethod = event.paymentMethod;
            orderClient.savePayMethodApplePay({}, jwt, {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                orderTotal: this.total.toFixed(2),
                response: JSON.stringify(this.applePayResponse),
                reviewPage: false,
            })
                .then(() => {
                    const lineItems = this.getLineItems();
                    session.completePaymentMethodSelection(
                        {
                            label: `${brand['display-name']}`,
                            amount: this.total.toFixed(2),
                            type: 'final',
                        },
                        lineItems,
                    );
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay onpaymentmethodselected',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on payment selected API call failed',
                    });
                });
        };
        session.onpaymentauthorized = async (event) => {
            this.applePayResponse.payment = event.payment;

            orderClient.authorizeApplePay({}, jwt, {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
                orderTotal: this.total.toFixed(2),
                response: JSON.stringify(this.applePayResponse),
                reviewPage: false,
            })
                .then((responseAuthApplePay) => {
                    mbpLogger.logDebug({
                        function: 'onpaymentauthorized',
                        module: 'GraphqlApplePay',
                        message: 'Graphql Apple Pay - response placeOrderApplePay',
                        responseAuthApplePay,
                    });

                    orderClient.placeOrderApplePay({}, jwt, {
                        orderId: this.orderId,
                    })
                        .then((response) => {
                            mbpLogger.logDebug({
                                function: 'onpaymentauthorized',
                                module: 'mbp-pwa-ui',
                                message: 'Graphql Apple Pay - response placeOrderApplePay',
                                response,
                            });
                            const status = window.ApplePaySession.STATUS_SUCCESS;
                            session.completePayment(status);

                            mbpLogger.logDebug({
                                function: 'onpaymentauthorized',
                                module: 'mbp-pwa-ui',
                                message: 'Graphql Apple Pay- before redirect',
                                statusAppleSession: window.ApplePaySession,
                            });

                            finishApplePay(history);
                        })
                        .catch((er) => {
                            mbpLogger.logError({
                                function: 'Graphql Apple Pay placeOrderApplePay',
                                module: 'mbp-pwa-ui',
                                jsError: er,
                                orderId: this.orderId,
                                message: 'on payment authorized API call failed',
                            });
                        });
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay authorizeApplePay',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        event,
                        objectSend: {
                            orderId: this.orderId,
                            orderItemId: this.orderItemId,
                            orderTotal: this.total.toFixed(2),
                            response: JSON.stringify(this.applePayResponse),
                            reviewPage: false,
                        },
                        message: 'on payment authorized API call failed',
                    });
                });
        };

        session.oncancel = () => {
            orderClient.cancelApplePay({}, jwt, {
                orderId: this.orderId,
                orderItemId: this.orderItemId,
            })
                .then(() => {
                    actionGetCart(this.orderId);
                })
                .catch((er) => {
                    mbpLogger.logError({
                        function: 'Graphql Apple Pay oncancel',
                        module: 'mbp-pwa-ui',
                        jsError: er,
                        message: 'on cancel API call failed',
                    });
                    actionGetCart(this.orderId);
                });
        };
        session.begin();
    };

    render() {
        const { cartEmpty } = this.state;
        const { applePayEnabled, location } = this.props;
        // Do not allow apple pay if there is already item in cart.

        if (applePayEnabled === 1 && cartEmpty === true) {
            return (
                <Grid container direction="column">
                    {/* Styles are inlined in this component because few are needed and to avoid HoC wrapping */}
                    {!location.pathname.includes('checkout') && <Grid item style={{ textAlign: 'center' }}>Or</Grid>}
                    <div
                        style={{
                            display: 'inline-block',
                            '-webkit-appearance': '-apple-pay-button',
                            '-apple-pay-button-style': 'black',
                            '-apple-pay-button-type': 'buy',
                            height: '40px',
                            width: '100%',
                        }}
                        onClick={location.pathname.includes('checkout') ? () => { this.handleCheckoutButtonClick(); } : () => { this.handlePDPButtonClick(); }}
                        onKeyDown={location.pathname.includes('checkout') ?  () => { this.handleCheckoutButtonClick(); } : () => { this.handlePDPButtonClick(); }}
                        role="link"
                        tabIndex={0}
                        id="applePayButtonID"
                        aria-label="apple pay button"
                    />
                </Grid>
            );
        }
        return null;
    }
}

const mapStateToProps = (state) => ({
    jwt: getAccessToken(state),
    brand: getBrand(state),
    itemInCart: getCartData(state),
    applePayEnabled: getApplePayStatus(state),
    numberOfOrderItems: getNoOfOrderItems(state),
    orderSummary: getOrderTotalSummary(state),
    orderId: getOrderId(state),
    movieItemInCart: getSelectedMovie(state),
    giftMessage: getGiftMessageInfo(state),
    appliedPromos: getAppliedPromotions(state),
});

const mapDispatchToProps = (dispatch) => ({
    clearCart: bindActionCreators(deleteItem, dispatch),
    saveCartData: bindActionCreators(loadCart, dispatch),
    checkApplePay: bindActionCreators(checkApplePayEnabled, dispatch),
    setOrderId: bindActionCreators(loadOrderId, dispatch),
    actionGetCart: bindActionCreators(getCart, dispatch),
    actionProcessNBCUMovie: bindActionCreators(processNBCUMovie, dispatch),
    finishApplePay: bindActionCreators(applePayOrderSuccess, dispatch),
    finishApplePayValidateMerchant: bindActionCreators(applePayOrderValidateMerchantSuccess, dispatch),
});

GraphqlApplePay.propTypes = {
    clearCart: func.isRequired,
    finishApplePay: func.isRequired,
    finishApplePayValidateMerchant: func.isRequired,
    saveCartData: func.isRequired,
    jwt: string.isRequired,
    actionGetCart: func.isRequired,
    brand: object.isRequired,
    history: object.isRequired,
    selectedSku: shape({
        __typename: string,
        id: string,
        name: string,
        ageVerifyFlag: string,
        minAge: string,
        prices: arrayOf(
            shape({
                type: string,
                value: number,
                __typename: string,
            }),
        ),
    }),
    applePayEnabled: number.isRequired,
    checkApplePay: func.isRequired,
    location: object.isRequired,
    numberOfOrderItems: number.isRequired,
    orderSummary: shape({
        orderTotalBalance: string,
        orderTotalBeforeTaxes: number,
        totalAdjustments: number,
        totalGiftCards: string,
        totalProduct: number,
        totalShipping: string,
        totalShippingAdjustments: number,
        totalTax: string,
    }),
    orderId: string,
    setOrderId: func.isRequired,
    movieItemInCart: object,
    actionProcessNBCUMovie: func.isRequired,
    giftMessage: string,
    appliedPromos: shape({
        appliedPromotions: shape({
            appliedAmount: string,
        }),
    }).isRequired,
};

GraphqlApplePay.defaultProps = {
    orderSummary: {},
    orderId: '',
    selectedSku: {},
    movieItemInCart: null,
    giftMessage: '',
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(GraphqlApplePay),
);
